<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <div class="page-titlerow">
          <h2 class="d-flex align-items-center" style="gap: 0.6rem;">
            <span>{{ headerTitle }}</span>
            <span v-if="!leagueConfig.active" class="badge bg-secondary" style="font-size: 0.7rem;">Inactive</span>
          </h2>
          <button type="button" class="page-helpbtn" :class="{ 'is-open': helpOpen }" :aria-expanded="helpOpen" @click="helpOpen = !helpOpen">
            <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
          </button>
        </div>

        <div v-show="helpOpen" class="page-help mb-3">
          <ul>
            <li>Confirmed matches for this calendar. To fix a match's details use <strong>Edit</strong> on the
              Matches page, and set up its sources on the Matches or <strong>Daily</strong> page.</li>
            <li>Click a match's <strong>Events</strong> count to expand it and choose which event each application
              uses for it. An event already taken by an overlapping match is greyed out, with a tooltip naming
              which match has it.</li>
            <li>Once an event is assigned, tick which of the match's configured channels carry it. One event can
              be carried by more than one channel.</li>
            <li><strong>Upcoming</strong> shows active matches that have not ended yet, <strong>All</strong> shows the
              whole calendar. A finished match, or an event that is on air, is read-only.</li>
          </ul>
        </div>

        <div class="d-flex justify-content-between align-items-center match-events-toolbar">
          <div class="d-flex align-items-center toolbar-btns" style="gap: 0.75rem;">
            <a role="button" class="procricket-btn--reverse mb-3" @click="backToCalendars">Back to Calendars</a>
            <a role="button" class="procricket-btn--reverse mb-3" @click="toggleAllEventsExpand">
              {{ allEventsExpanded ? 'Collapse' : 'Expand' }} All Match Events
            </a>
          </div>
          <div class="d-flex align-items-center" style="gap: 0.75rem; width: 40%; justify-content: flex-end;">
            <span class="small text-muted mb-3 text-nowrap">{{ shownCountText }}</span>
            <div class="view-switch mb-3" role="group" aria-label="Matches to show" title="Upcoming = active matches that have not ended yet">
              <a role="button" :class="{ active: matchFilter === 'upcoming' }" @click="matchFilter = 'upcoming'">Upcoming</a>
              <a role="button" :class="{ active: matchFilter === 'all' }" @click="matchFilter = 'all'">All</a>
            </div>
            <input type="text" class="form-control mb-3 match-search-input" placeholder="Search matches..." v-model="searchText" style="width: 50%; position: relative;">
          </div>
        </div>
        <div v-if="loadError !== ''" class="error-message mb-3">{{ loadError }}</div>
        <div class="table-responsive">
          <table class="table table-striped table-bordered match-events-table">
            <thead>
            <tr>
              <th class="col-1" @click="toggleSort('calMatchId')">Id</th>
              <th class="col-1">Day</th>
              <th class="col-1 team-cell" @click="toggleSort('team1')">Team 1</th>
              <th class="col-1 team-cell" @click="toggleSort('team2')">Team 2</th>
              <th class="col-2" @click="toggleSort('venue')">Venue</th>
              <th class="col-2" @click="toggleSort('startTime')">Start ({{ tz }})</th>
              <th class="col-2" @click="toggleSort('endTime')">End ({{ tz }})</th>
              <th class="col-1" @click="toggleSort('format')">Format</th>
              <th class="col-1" @click="toggleSort('calStatus')">Cal Status</th>
              <th class="col-1" @click="toggleSort('status')">Status</th>
              <th class="col-1" @click="toggleSort('active')">Active</th>
              <th class="col-1">Events</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="match in sortData" :key="match.calMatchId">
              <tr :id="'match-row-' + match.calMatchId" class="match-summary-row" :class="{ 'row-excluded': !match.active, 'row-highlighted': match.calMatchId === highlightedMatchId }">
                <td class="mobile-match-card" :colspan="12" @click="toggleEventsExpand(match.calMatchId)">
                  <div class="mobile-card-top">
                    <span class="mobile-card-teams text-truncate">{{ truncateText(match.team1, 40) }} <span class="mobile-vs">vs</span> {{ truncateText(match.team2, 40) }}</span>
                    <span class="chevron">{{ isEventsExpanded(match.calMatchId) ? '▾' : '▸' }}</span>
                  </div>
                  <div class="mobile-card-meta text-muted small">{{ formatShortRange(match) }}</div>
                  <div class="mobile-card-bottom">
                    <span v-if="!match.active" class="badge bg-secondary">Inactive</span>
                    <span v-else></span>
                    <span class="mobile-card-events">{{ configuredEventCountFor(match.calMatchId) }} event{{ configuredEventCountFor(match.calMatchId) === 1 ? '' : 's' }}</span>
                  </div>
                </td>
                <td class="col-1 text-truncate">{{ truncateText(match.calMatchId, 30) }}</td>
                <td class="col-1 text-truncate">{{ dayLabel(match) }}</td>
                <td class="col-1 team-cell text-truncate">{{ truncateText(match.team1, 40) }}</td>
                <td class="col-1 team-cell text-truncate">{{ truncateText(match.team2, 40) }}</td>
                <td class="col-2 text-truncate">{{ truncateText(match.venue, 40) }}</td>
                <td class="col-2 text-nowrap">{{ formatDateTime(match.startTime) }}</td>
                <td class="col-2 text-nowrap">{{ formatDateTime(match.endTime) }}</td>
                <td class="col-1 text-truncate">{{ truncateText(match.format, 20) }}</td>
                <td class="col-1 text-truncate">{{ truncateText(match.calStatus, 20) }}</td>
                <td class="col-1 text-truncate">{{ truncateText(match.status, 10) }}</td>
                <td class="col-1 text-truncate">{{ truncateText(match.active, 10) }}</td>
                <td class="col-1 text-center text-muted small clickable-cell" @click="toggleEventsExpand(match.calMatchId)">
                  {{ configuredEventCountFor(match.calMatchId) }} <span class="chevron">{{ isEventsExpanded(match.calMatchId) ? '▾' : '▸' }}</span>
                </td>
              </tr>
              <tr>
                <td :colspan="12" class="expand-cell">
                  <transition name="expand">
                    <div v-show="isEventsExpanded(match.calMatchId)" class="expand-wrapper p-2 cal-sources-panel">
                      <div v-if="applicationCountsForMatch(match.calMatchId).length" class="app-counts-row mb-2">
                        <span v-for="ac in applicationCountsForMatch(match.calMatchId)" :key="ac.application" class="app-count-pill">
                          {{ ac.application }}: {{ ac.events }} event{{ ac.events === 1 ? '' : 's' }} &middot; {{ ac.channels }} channel{{ ac.channels === 1 ? '' : 's' }}
                        </span>
                      </div>
                      <MatchEventSlotEditor
                          v-for="slot in visibleEventSlotsFor(match)" :key="slot.key"
                          :match-event="slot.matchEvent"
                          :application-list="sportApplicationList"
                          :events-by-application="eventsByApplication"
                          :events-by-id="eventsById"
                          :taken-event-windows="takenEventWindowsForMatch(match, slot.matchEvent ? slot.matchEvent.matchEventId : null)"
                          :pad-limits="eventPadLimitsForMatch(match)"
                          :read-only="!matchEditable(match)"
                          :saving="!!matchEventSaving[match.calMatchId + '-' + slot.key]"
                          :error-message="matchEventErrors[match.calMatchId + '-' + slot.key] || ''"
                          :max-pad-setup-minutes="calendarStatic.maxPadSetupMinutes"
                          :channel-options="channelsByMatch[match.calMatchId] || []"
                          :linked-channel-ids="slot.matchEvent ? (channelLinksByMatchEvent[slot.matchEvent.matchEventId] || []) : []"
                          :disabled-channel-ids="slot.matchEvent ? channelsClaimedElsewhere(match.calMatchId, slot.matchEvent.eventId, slot.matchEvent.matchEventId) : []"
                          :channel-saving="!!(slot.matchEvent && channelLinkSaving[slot.matchEvent.matchEventId])"
                          :channel-error-message="(slot.matchEvent && channelLinkErrors[slot.matchEvent.matchEventId]) || ''"
                          @save="(payload) => onMatchEventSave(match.calMatchId, slot.key, payload)"
                          @remove="(payload) => onMatchEventRemove(match.calMatchId, slot.key, payload)"
                          @save-channels="(payload) => onSaveChannels(slot.matchEvent && slot.matchEvent.matchEventId, payload)">
                      </MatchEventSlotEditor>
                      <a v-if="matchEditable(match)" role="button" class="text-link-sm" @click="addEventSlot(match.calMatchId)">
                        + Add match event
                      </a>
                    </div>
                  </transition>
                </td>
              </tr>
            </template>
            <tr v-if="!loading && sortData.length === 0">
              <td :colspan="12" class="text-center text-muted">
                {{ matchFilter === 'upcoming' && calMatchesList.length > 0 && searchText.trim() === ''
                    ? 'No upcoming matches in this calendar. Switch to All to see past and inactive ones.'
                    : 'No matches found for this calendar.' }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";
import MatchEventSlotEditor from "@/components/MatchEventSlotEditor.vue";
import { MatchEvent } from "@/model/models";
import { offsetIsoMinutes, formatLocalDateTime, formatLocalShortRange, formatLocalDayHm, localTimezoneLabel } from "@/utils/general-functions";

export default {
  name: "MatchEvents",
  components: {
    Header,
    MatchEventSlotEditor
  },
  data() {
    return {
      tz: localTimezoneLabel(),
      calLeagueId: this.$route.params.calLeagueId,
      helpOpen: false,
      leagueName: this.$route.params.name,
      applicationSports: null,
      leagueConfig: {
        shortName: this.$route.params.shortName,
        sport: this.$route.params.sport,
        active: this.$route.params.active === 'true' || this.$route.params.active === true
      },
      searchText: '',
      matchFilter: 'upcoming',
      nowMs: Date.now(),
      calMatchesList: [],
      calendarStatic: {
        languages: [],
        defaultLeadMinutes: 15,
        defaultTrailMinutes: 15,
        maxPadSetupMinutes: 720
      },
      eventsList: [],
      applicationList: [],
      matchEventsList: [],
      calChannelsList: [],
      calEventChannelsList: [],
      channelLinkSaving: {},
      channelLinkErrors: {},
      crossLeagueOccupiedEvents: [],
      matchEventSaving: {},
      matchEventErrors: {},
      sortColumn: '',
      sortDirection: 'asc',
      loading: false,
      loadError: '',
      highlightedMatchId: null,
      expandedEventsIds: {},
      allEventsExpanded: false,
      visibleEventSlotCount: {},
      removedMatchEventIds: {}
    };
  },
  computed: {
    headerTitle() {
      const shortName = this.leagueConfig.shortName ? ` (${this.leagueConfig.shortName})` : '';
      return `Match Events${this.leagueName ? ` — ${this.leagueName}${shortName}` : ''}`;
    },
    filteredCalMatchesList() {
      const search = this.searchText.toLowerCase().trim();
      let filteredList = this.calMatchesList.filter((match) => {
        if (this.matchFilter === 'upcoming' && !this.isUpcomingMatch(match)) return false;
        return (
            (match.calMatchId !== undefined && match.calMatchId !== null && match.calMatchId.toString().includes(search)) ||
            (match.team1 && match.team1.toString().toLowerCase().includes(search)) ||
            (match.team2 && match.team2.toString().toLowerCase().includes(search)) ||
            (match.venue && match.venue.toString().toLowerCase().includes(search)) ||
            (match.format && match.format.toString().toLowerCase().includes(search)) ||
            (match.calStatus && match.calStatus.toString().toLowerCase().includes(search))
        );
      });

      if (this.sortColumn) {
        const direction = this.sortDirection === 'asc' ? 1 : -1;
        filteredList = filteredList.sort((a, b) => {
          const valueA = a[this.sortColumn];
          const valueB = b[this.sortColumn];

          if (valueA < valueB) return -1 * direction;
          if (valueA > valueB) return 1 * direction;
          return 0;
        });
      }
      return filteredList;
    },
    sortData() {
      return this.filteredCalMatchesList;
    },
    sportApplicationList() {
      const sport = this.leagueConfig.sport;
      if (!sport || !this.applicationSports) return this.applicationList;
      const usedApplications = new Set();
      for (const me of this.matchEventsList) {
        const app = this.eventsById[me.eventId]?.application;
        if (app) usedApplications.add(app);
      }
      return this.applicationList.filter(app => this.applicationSports[app] === sport || usedApplications.has(app));
    },
    upcomingCount() {
      return this.calMatchesList.filter(m => this.isUpcomingMatch(m)).length;
    },
    shownCountText() {
      return this.matchFilter === 'upcoming'
          ? `${this.upcomingCount} upcoming of ${this.calMatchesList.length}`
          : `${this.calMatchesList.length} matches`;
    },
    eventsById() {
      const map = {};
      for (const ev of this.eventsList) map[ev.eventId] = ev;
      return map;
    },
    eventsByApplication() {
      const map = {};
      for (const ev of this.eventsList) {
        const app = ev.application || '—';
        if (!map[app]) map[app] = [];
        map[app].push(ev);
      }
      return map;
    },
    matchEventsByMatch() {
      const map = {};
      for (const me of this.matchEventsList) {
        if (!map[me.calMatchId]) map[me.calMatchId] = [];
        map[me.calMatchId].push(me);
      }
      return map;
    },
    channelsByMatch() {
      const map = {};
      for (const ch of this.calChannelsList) {
        if (!ch.active) continue;
        if (!map[ch.calMatchId]) map[ch.calMatchId] = [];
        map[ch.calMatchId].push(ch);
      }
      for (const list of Object.values(map)) {
        list.sort((a, b) => (a.channelName || '').localeCompare(b.channelName || ''));
      }
      return map;
    },
    channelLinksByMatchEvent() {
      const map = {};
      for (const link of this.calEventChannelsList) {
        if (!link.active) continue;
        if (!map[link.matchEventId]) map[link.matchEventId] = [];
        map[link.matchEventId].push(link.calChannelId);
      }
      return map;
    },
    channelClaimsByMatch() {
      const map = {};
      for (const me of this.matchEventsList) {
        if (!me.active || !me.eventId) continue;
        const application = this.eventsById[me.eventId]?.application;
        if (!application) continue;
        const linkedIds = this.channelLinksByMatchEvent[me.matchEventId] || [];
        if (!linkedIds.length) continue;
        if (!map[me.calMatchId]) map[me.calMatchId] = {};
        if (!map[me.calMatchId][application]) map[me.calMatchId][application] = {};
        for (const calChannelId of linkedIds) {
          map[me.calMatchId][application][calChannelId] = me.matchEventId;
        }
      }
      return map;
    },
    takenEventWindowsByEvent() {
      const map = {};
      for (const me of this.matchEventsList) {
        if (!me.eventId || !me.active) continue;
        const match = this.calMatchesList.find(m => m.calMatchId === me.calMatchId);
        if (!match || !match.startTime) continue;
        if (!map[me.eventId]) map[me.eventId] = [];
        map[me.eventId].push({
          calMatchId: me.calMatchId,
          busyStart: new Date(offsetIsoMinutes(match.startTime, -(me.eventPadSetup ?? this.calendarStatic.maxPadSetupMinutes))).getTime(),
          busyEnd: new Date(match.endTime || match.startTime).getTime()
        });
      }
      for (const oe of this.crossLeagueOccupiedEvents) {
        if (!oe.eventId) continue;
        if (!map[oe.eventId]) map[oe.eventId] = [];
        map[oe.eventId].push({
          calMatchId: null,
          busyStart: new Date(oe.busyStart).getTime(),
          busyEnd: new Date(oe.busyEnd).getTime()
        });
      }
      return map;
    }
  },
  created() {
    this.doLoadAll();
    this.sortColumn = 'startTime';
  },
  mounted() {
    this._nowTimer = setInterval(() => { this.nowMs = Date.now(); }, 60 * 1000);
  },
  beforeUnmount() {
    clearInterval(this._nowTimer);
    clearTimeout(this._highlightTimer);
  },
  methods: {
    doLoadAll() {
      if (!this.calLeagueId) return;
      this.loading = true;
      this.loadError = '';
      this.loadApplicationList();
      this.loadApplicationSports();
      this.loadCalendarStaticData();
      this.loadCalEventChannels();

      const calLeagueId = Number(this.calLeagueId);
      Promise.all([
        AdminService.getCalMatchesList(calLeagueId),
        AdminService.getEventsList(true),
        AdminService.getMatchEventsList(calLeagueId),
        AdminService.getOccupiedEventsOutsideLeague(calLeagueId),
        AdminService.getCalChannelsList(calLeagueId)
      ]).then(([matches, events, matchEvents, occupiedElsewhere, channels]) => {
        this.calMatchesList = matches;
        this.eventsList = events || [];
        this.matchEventsList = matchEvents || [];
        this.crossLeagueOccupiedEvents = occupiedElsewhere || [];
        this.calChannelsList = channels || [];
        this.applyOpenMatchQuery();
      }).catch((error) => {
        console.error("Error loading matches/events:", error);
        this.loadError = this.extractErrorMessage(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    loadApplicationList() {
      const stored = localStorage.getItem('applicationList');
      if (stored) {
        this.applicationList = JSON.parse(stored);
      } else {
        AdminService.getApplications().then(() => {
          this.applicationList = JSON.parse(localStorage.getItem('applicationList') || '[]');
        }).catch((error) => {
          console.error("Error loading application list:", error);
        });
      }
    },
    loadApplicationSports() {
      AdminService.getApplicationList().then((list) => {
        const map = {};
        for (const app of list) map[app.application] = app.sport;
        this.applicationSports = map;
      }).catch((error) => {
        console.error("Error loading application sports:", error);
      });
    },
    loadCalendarStaticData() {
      AdminService.getCalendarStaticData().then((data) => {
        this.calendarStatic = data;
      }).catch((error) => {
        console.error("Error loading calendar static data:", error);
      });
    },
    loadCalEventChannels() {
      const fetch = AdminService.getCalEventChannelsList(Number(this.calLeagueId));
      fetch.then((links) => {
        this.calEventChannelsList = links || [];
      }).catch((error) => {
        console.error("Error loading cal-event-channel links:", error);
      });
    },
    refetchCalEventChannels() {
      const fetch = AdminService.getCalEventChannelsList(Number(this.calLeagueId));
      return fetch.then((links) => {
        this.calEventChannelsList = links || [];
      });
    },
    focusMatch(calMatchId) {
      this.expandedEventsIds = { ...this.expandedEventsIds, [calMatchId]: true };
      this.highlightedMatchId = calMatchId;
      clearTimeout(this._highlightTimer);
      this._highlightTimer = setTimeout(() => {
        if (this.highlightedMatchId === calMatchId) this.highlightedMatchId = null;
      }, 4000);
      this.$nextTick(() => {
        const el = document.getElementById('match-row-' + calMatchId);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    applyOpenMatchQuery() {
      const raw = this.$route.query.openMatch;
      if (!raw) return;
      const targetId = Number(raw);
      const target = this.calMatchesList.find(m => m.calMatchId === targetId);
      if (target) {
        if (!this.isUpcomingMatch(target)) this.matchFilter = 'all';
        this.focusMatch(targetId);
      }
    },
    effectiveEnd(match) {
      return match.endTime || match.startTime;
    },
    matchFinished(match) {
      const end = new Date(this.effectiveEnd(match)).getTime();
      return !isNaN(end) && end <= this.nowMs;
    },
    matchEditable(match) {
      return !this.matchFinished(match);
    },
    isUpcomingMatch(match) {
      return !!match.active && !this.matchFinished(match);
    },
    toggleEventsExpand(calMatchId) {
      this.expandedEventsIds = { ...this.expandedEventsIds, [calMatchId]: !this.expandedEventsIds[calMatchId] };
    },
    isEventsExpanded(calMatchId) {
      return !!this.expandedEventsIds[calMatchId];
    },
    toggleAllEventsExpand() {
      this.allEventsExpanded = !this.allEventsExpanded;
      if (this.allEventsExpanded) {
        const next = {};
        for (const m of this.sortData) next[m.calMatchId] = true;
        this.expandedEventsIds = next;
      } else {
        this.expandedEventsIds = {};
      }
    },
    isMatchEventConfigured(me) {
      return !!(me && me.eventId && me.active);
    },
    configuredEventCountFor(calMatchId) {
      return (this.matchEventsByMatch[calMatchId] || []).filter(this.isMatchEventConfigured).length;
    },
    matchLabel(match) {
      return `Match #${match.calMatchId}, ${this.formatShortRange(match)}`;
    },
    formatShortRange(match) {
      return formatLocalShortRange(match.startTime, match.endTime);
    },
    formatLocalHm(ms) {
      return formatLocalDayHm(ms);
    },
    takenEventWindowsForMatch(match, ownMatchEventId) {
      const thisBusyStart = new Date(match.startTime).getTime();
      const thisBusyEnd = new Date(match.endTime || match.startTime).getTime();
      const result = {};
      for (const [eventId, windows] of Object.entries(this.takenEventWindowsByEvent)) {
        const conflict = windows.find(w =>
            w.calMatchId !== match.calMatchId &&
            thisBusyStart < w.busyEnd && w.busyStart < thisBusyEnd
        );
        if (conflict) {
          let label;
          if (conflict.calMatchId !== null) {
            const conflictMatch = this.calMatchesList.find(m => m.calMatchId === conflict.calMatchId);
            label = conflictMatch ? this.matchLabel(conflictMatch) : `Match #${conflict.calMatchId}`;
          } else {
            label = `another league, until ${this.formatLocalHm(conflict.busyEnd)}`;
          }
          result[eventId] = { calMatchId: conflict.calMatchId, label };
        }
      }
      for (const me of (this.matchEventsByMatch[match.calMatchId] || [])) {
        if (!me.eventId || !me.active || me.matchEventId === ownMatchEventId) continue;
        if (result[me.eventId]) continue;
        result[me.eventId] = { calMatchId: match.calMatchId, label: 'another slot on this match' };
      }
      return result;
    },
    eventPadLimitsForMatch(match) {
      const thisStart = new Date(match.startTime).getTime();
      const ceiling = this.calendarStatic.maxPadSetupMinutes;
      const result = {};
      for (const [eventId, windows] of Object.entries(this.takenEventWindowsByEvent)) {
        let prev = null;
        for (const w of windows) {
          if (w.calMatchId === match.calMatchId || w.busyEnd > thisStart) continue;
          if (!prev || w.busyEnd > prev.busyEnd) prev = w;
        }
        if (!prev) continue;
        const maxPad = Math.min(ceiling, Math.floor((thisStart - prev.busyEnd) / 60000));
        if (maxPad >= ceiling) continue;
        const prevMatch = prev.calMatchId !== null ? this.calMatchesList.find(m => m.calMatchId === prev.calMatchId) : null;
        const who = prevMatch ? this.matchLabel(prevMatch) : (prev.calMatchId !== null ? `Match #${prev.calMatchId}` : 'another league');
        result[eventId] = { maxPad, label: `${who} ends ${this.formatLocalHm(prev.busyEnd)}` };
      }
      return result;
    },
    visibleMatchEventRows(calMatchId) {
      return (this.matchEventsByMatch[calMatchId] || [])
          .filter(me => !this.removedMatchEventIds[me.matchEventId]);
    },
    getVisibleEventSlotCount(calMatchId) {
      if (this.visibleEventSlotCount[calMatchId] !== undefined) {
        return this.visibleEventSlotCount[calMatchId];
      }
      return Math.max(this.visibleMatchEventRows(calMatchId).length, 1);
    },
    visibleEventSlotsFor(match) {
      const rows = this.visibleMatchEventRows(match.calMatchId);
      const visibleCount = this.getVisibleEventSlotCount(match.calMatchId);
      const slots = rows.map((me, i) => ({ key: 'me-' + me.matchEventId, index: i + 1, matchEvent: me }));
      if (!this.matchEditable(match)) return slots;
      for (let i = slots.length; i < visibleCount; i++) {
        slots.push({ key: 'new-me-' + i, index: i + 1, matchEvent: null });
      }
      return slots;
    },
    addEventSlot(calMatchId) {
      const current = this.getVisibleEventSlotCount(calMatchId);
      this.visibleEventSlotCount = { ...this.visibleEventSlotCount, [calMatchId]: current + 1 };
    },
    applicationCountsForMatch(calMatchId) {
      const rows = (this.matchEventsByMatch[calMatchId] || []).filter(me => me.eventId && me.active);
      const counts = {};
      for (const me of rows) {
        const app = this.eventsById[me.eventId]?.application || '—';
        if (!counts[app]) counts[app] = { events: 0, channels: 0 };
        counts[app].events += 1;
        counts[app].channels += (this.channelLinksByMatchEvent[me.matchEventId] || []).length;
      }
      return Object.keys(counts).sort().map(application => ({ application, ...counts[application] }));
    },
    channelsClaimedElsewhere(calMatchId, eventId, excludeMatchEventId) {
      const application = this.eventsById[eventId]?.application;
      if (!application) return [];
      const claims = (this.channelClaimsByMatch[calMatchId] || {})[application] || {};
      return Object.entries(claims)
          .filter(([, matchEventId]) => matchEventId !== excludeMatchEventId)
          .map(([calChannelId]) => Number(calChannelId));
    },
    onSaveChannels(matchEventId, { calChannelIds }) {
      if (!matchEventId) return;
      this.channelLinkSaving = { ...this.channelLinkSaving, [matchEventId]: true };
      this.channelLinkErrors = { ...this.channelLinkErrors, [matchEventId]: '' };
      AdminService.setCalEventChannels(matchEventId, calChannelIds).then(() => {
        return this.refetchCalEventChannels();
      }).catch((error) => {
        this.channelLinkErrors = { ...this.channelLinkErrors, [matchEventId]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.channelLinkSaving = { ...this.channelLinkSaving, [matchEventId]: false };
      });
    },
    upsertMatchEventRow(calMatchId, payload) {
      const existing = payload.matchEventId
          ? this.matchEventsList.find(me => me.matchEventId === payload.matchEventId)
          : this.matchEventsList.find(me => me.calMatchId === calMatchId && me.eventId === payload.eventId);

      const matchEvent = new MatchEvent(
          existing ? existing.matchEventId : undefined,
          calMatchId,
          payload.eventId,
          payload.padSetup,
          existing ? existing.status : false,
          payload.active
      );
      return AdminService.upsertMatchEvent(matchEvent).then(() => matchEvent);
    },
    refetchMatchEvents() {
      const fetch = AdminService.getMatchEventsList(Number(this.calLeagueId));
      return fetch.then((matchEvents) => {
        this.matchEventsList = matchEvents;
      });
    },
    saveMatchEvent(calMatchId, payload) {
      return this.upsertMatchEventRow(calMatchId, payload).then((matchEvent) => {
        return this.refetchMatchEvents().then(() => matchEvent);
      });
    },
    onMatchEventSave(calMatchId, slotKey, payload) {
      const key = `${calMatchId}-${slotKey}`;
      this.matchEventSaving = { ...this.matchEventSaving, [key]: true };
      this.matchEventErrors = { ...this.matchEventErrors, [key]: '' };
      this.saveMatchEvent(calMatchId, payload).catch((error) => {
        this.matchEventErrors = { ...this.matchEventErrors, [key]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.matchEventSaving = { ...this.matchEventSaving, [key]: false };
      });
    },
    onMatchEventRemove(calMatchId, slotKey, payload) {
      const key = `${calMatchId}-${slotKey}`;
      const matchEventId = payload.matchEventId;
      const previousCount = this.getVisibleEventSlotCount(calMatchId);
      this.visibleEventSlotCount = { ...this.visibleEventSlotCount, [calMatchId]: Math.max(previousCount - 1, 0) };

      if (!matchEventId) return;

      this.removedMatchEventIds = { ...this.removedMatchEventIds, [matchEventId]: true };
      this.matchEventSaving = { ...this.matchEventSaving, [key]: true };
      this.matchEventErrors = { ...this.matchEventErrors, [key]: '' };
      this.saveMatchEvent(calMatchId, { ...payload, active: false }).catch((error) => {
        const restored = { ...this.removedMatchEventIds };
        delete restored[matchEventId];
        this.removedMatchEventIds = restored;
        this.visibleEventSlotCount = { ...this.visibleEventSlotCount, [calMatchId]: previousCount };
        this.matchEventErrors = { ...this.matchEventErrors, [key]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.matchEventSaving = { ...this.matchEventSaving, [key]: false };
      });
    },
    dayLabel(match) {
      if (!match.parentUid) return '—';
      const siblings = this.calMatchesList
          .filter(m => m.parentUid === match.parentUid && m.calLeagueId === match.calLeagueId)
          .sort((a, b) => (a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0));
      if (siblings.length < 2) return '—';
      const idx = siblings.findIndex(m => m.calMatchId === match.calMatchId);
      return `Day ${idx + 1} of ${siblings.length}`;
    },
    formatDateTime(iso) {
      return formatLocalDateTime(iso);
    },
    truncateText(text, limit) {
      if (text === undefined || text === null) {
        return null
      } else {
        return String(text).length > limit ? String(text).substring(0, limit) + '...' : String(text);
      }
    },
    backToCalendars() {
      this.$router.push({ name: 'CalLeagues' });
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    }
  },
};
</script>
<style src="../../assets/style.css">
</style>
